import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { trpc } from '@/lib/trpc';
import type { WorkbenchNode } from './workbench-tree-row';

/**
 * Confirm a drag-to-re-parent, and offer to renumber.
 *
 * The hierarchy lives in parentId, but the reports bucket on the code prefix —
 * so a branch moved without renumbering reports under its old parent. Both
 * choices are legitimate, which is why this asks instead of deciding: keeping
 * the codes preserves historical report grouping, renumbering makes the chart
 * internally consistent.
 */
export function ReparentDialog({
  dragged,
  target,
  onClose,
}: {
  dragged: WorkbenchNode;
  target: WorkbenchNode;
  onClose: () => void;
}) {
  const { t } = useTranslation('accounting');
  const [warnings, setWarnings] = useState<string[]>([]);
  const utils = trpc.useUtils();

  const { data: suggestion } = trpc.accountWorkbench.suggestCode.useQuery({
    parentCode: target.code,
  });

  const reparent = trpc.accountWorkbench.reparent.useMutation({
    onSuccess: (result) => {
      utils.account.getTree.invalidate();
      utils.account.list.invalidate();
      utils.accountWorkbench.health.invalidate();
      utils.accountWorkbench.changes.invalidate();
      if (result.warnings.length > 0) setWarnings(result.warnings);
      else onClose();
    },
  });

  const move = (renumber: boolean) =>
    reparent.mutate({ id: dragged.id, newParentId: target.id, renumber });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-w-lg flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('reparent.title')}</DialogTitle>
          <DialogDescription>
            {t('reparent.description', {
              dragged: `${dragged.code} ${dragged.name}`,
              target: `${target.code} ${target.name}`,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
          <div className="border-border divide-border divide-y border text-sm">
            <div className="p-3">
              <p className="font-medium">{t('reparent.keepCodes')}</p>
              <p className="text-muted-foreground mt-0.5 text-xs">
                {t('reparent.keepCodesHint', { code: dragged.code })}
              </p>
            </div>
            <div className="p-3">
              <p className="font-medium">{t('reparent.renumber')}</p>
              <p className="text-muted-foreground mt-0.5 text-xs">
                {suggestion
                  ? t('reparent.renumberHint', { from: dragged.code, to: suggestion.next })
                  : t('reparent.renumberLoading')}
              </p>
            </div>
          </div>

          {dragged.type !== target.type && (
            <p className="bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
              {t('reparent.typeMismatch', { dragged: dragged.type, target: target.type })}
            </p>
          )}

          {warnings.length > 0 && (
            <div className="bg-amber-50 p-3 dark:bg-amber-950/30">
              <ul className="list-inside list-disc space-y-0.5 text-xs text-amber-800 dark:text-amber-300">
                {warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          {reparent.error && (
            <p className="bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
              {reparent.error.message}
            </p>
          )}
        </div>

        <DialogFooter>
          {warnings.length > 0 ? (
            <Button onClick={onClose}>{t('accountModal.acknowledge')}</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={reparent.isPending}>
                {t('accountModal.cancel')}
              </Button>
              <Button variant="outline" onClick={() => move(false)} disabled={reparent.isPending}>
                {reparent.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('reparent.keepCodesAction')}
              </Button>
              <Button onClick={() => move(true)} disabled={reparent.isPending || !suggestion}>
                {t('reparent.renumberAction')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
